/* global XMLHttpRequest */
/* ============================================================================
   Sarah Louise Interiors (SL Home 2.1) — Custom Judge.me review components (behaviour)
   ----------------------------------------------------------------------------
   Web-component islands + a normalized Judge.me data adapter.

   DATA SOURCES (in priority order, resolved per component):
     1. data-reviews-endpoint  — optional JSON proxy you control (private token
        stays server-side). If set, it must return normalized JSON:
          product list : GET {endpoint}?type=product&product_id=ID&page=N
          featured     : GET {endpoint}?type=featured&country=GB
        Each returns { reviews:[...], histogram:{5:n,...}, count, average, more }.
        This is the ONLY path that can reliably deliver the fit aggregate and
        reviewer-country geo-targeting (see beyours-summer26-approach + the
        Judge.me API note: the public token cannot return those as clean data).
     2. Public cache-widget  — default. Uses window.jdgmSettings.public_token
        (injected by the judgeme_core app embed) against
        https://cache.judge.me/widgets/shopify/{shop_domain}. Returns rendered
        widget HTML which we parse best-effort into the same normalized shape.
        Per-review data (rating, author, body, date, verified, photo, location,
        fit answer) is parsed where the widget exposes it; anything missing
        degrades gracefully (feature hidden, never broken).

   Everything is wrapped so a failed/empty fetch leaves the server-rendered
   Liquid fallback (summary + empty state) intact and hides optional modules.
   ============================================================================ */
(function () {
  'use strict';

  if (window.SLReviews) return;

  /* ---- config -------------------------------------------------------------- */
  function jdgm() { return window.jdgmSettings || window.jdgm || {}; }

  function shopDomain(el) {
    return (el && el.dataset.shopDomain) ||
      jdgm().shop_domain ||
      (window.Shopify && window.Shopify.shop) || '';
  }
  function publicToken(el) {
    return (el && el.dataset.publicToken) ||
      jdgm().public_token || jdgm().widget_token || '';
  }

  /* ---- seeded random (mulberry32), seeded by day of year ------------------- */
  function dayOfYearSeed() {
    var now = new Date();
    var start = new Date(now.getFullYear(), 0, 0);
    var diff = now - start;
    return now.getFullYear() * 1000 + Math.floor(diff / 86400000);
  }
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  // Deterministic shuffle for the day; extra salt keeps cart/module independent.
  function seededPick(arr, n, salt) {
    if (!arr || !arr.length) return [];
    var rng = mulberry32(dayOfYearSeed() + (salt || 0));
    var copy = arr.slice();
    for (var i = copy.length - 1; i > 0; i--) {
      var j = Math.floor(rng() * (i + 1));
      var tmp = copy[i]; copy[i] = copy[j]; copy[j] = tmp;
    }
    return copy.slice(0, n);
  }

  /* ---- 24h localStorage cache --------------------------------------------- */
  var TTL = 24 * 60 * 60 * 1000;
  function cacheGet(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || (Date.now() - obj.t) > TTL) { localStorage.removeItem(key); return null; }
      return obj.v;
    } catch (e) { return null; }
  }
  function cacheSet(key, value) {
    try { localStorage.setItem(key, JSON.stringify({ t: Date.now(), v: value })); } catch (e) {}
  }

  /* ---- helpers ------------------------------------------------------------- */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function clampPct(r) { var p = (Number(r) / 5) * 100; return Math.max(0, Math.min(100, p)); }
  // accept http(s)://, protocol-relative //cdn… and root-relative /cdn… image
  // URLs (Shopify image_url often returns protocol-relative); block script URLs.
  function imgOk(u) { return !!u && !/^\s*(javascript|data|vbscript):/i.test(u); }

  function starsHTML(rating) {
    var pct = clampPct(rating).toFixed(1);
    var label = (Math.round(Number(rating) * 10) / 10) + ' out of 5 stars';
    return '<span class="sl-stars" style="--rev-fill:' + pct + '%" role="img" aria-label="' + label + '">' +
      '<span class="sl-stars__base" aria-hidden="true">★★★★★</span>' +
      '<span class="sl-stars__fill" aria-hidden="true">★★★★★</span>' +
      '</span>';
  }

  // Short codes where they read cleanly, full country name otherwise.
  var SHORT = {
    'GB': 'UK', 'UK': 'UK', 'US': 'US', 'USA': 'US', 'AU': 'AU', 'IE': 'IE'
  };
  var NAMES = {
    'FR': 'France', 'DE': 'Germany', 'ES': 'Spain', 'IT': 'Italy', 'NL': 'Netherlands',
    'CA': 'Canada', 'NZ': 'New Zealand', 'AE': 'United Arab Emirates', 'IN': 'India',
    'SE': 'Sweden', 'NO': 'Norway', 'DK': 'Denmark', 'CH': 'Switzerland', 'BE': 'Belgium',
    'SG': 'Singapore', 'HK': 'Hong Kong', 'JP': 'Japan', 'ZA': 'South Africa'
  };
  function countryLabel(code) {
    if (!code) return '';
    var c = String(code).trim().toUpperCase();
    if (SHORT[c]) return SHORT[c];
    if (NAMES[c]) return NAMES[c];
    return code.length <= 3 ? c : code; // unknown short code -> uppercase; full name passes through
  }
  function shopifyCountry() {
    try {
      if (window.Shopify && window.Shopify.country) return window.Shopify.country;
    } catch (e) {}
    var meta = document.querySelector('[data-sl-country]');
    return (meta && meta.getAttribute('data-sl-country')) || '';
  }

  /* ---- normalized review shape -------------------------------------------- */
  // { rating, title, body, author, location, country, date, dateLabel,
  //   verified, fit, picture, productTitle }

  /* ---- best-effort parser for Judge.me widget HTML ------------------------ */
  function parseReviewsFromHTML(html) {
    if (!html) return [];
    try { return parseReviewsFromRoot(new DOMParser().parseFromString(html, 'text/html')); }
    catch (e) { return []; }
  }
  function parseWidgetEl(el) {
    return el ? parseReviewsFromRoot(el) : [];
  }
  function parseReviewsFromRoot(doc) {
    var out = [];
    if (!doc) return out;
    var nodes = doc.querySelectorAll('.jdgm-rev, [data-review-id], .jdgm-carousel-item');
    Array.prototype.forEach.call(nodes, function (n) {
      var ratingEl = n.querySelector('[data-score], .jdgm-rev__rating');
      var rawScore = ratingEl && ratingEl.getAttribute('data-score');
      var rating = rawScore ? Number(rawScore) : NaN;  // treat missing/0/'' as unknown -> glyph fallback
      if (isNaN(rating) || rating === 0) {
        // count filled star glyphs as a fallback
        var filled = n.querySelectorAll('.jdgm--on, .jdgm-star--on');
        rating = filled.length || 5;
      }
      var titleEl = n.querySelector('.jdgm-rev__title, .jdgm-carousel-item__review-title, .jdgm-carousel-item__title');
      var bodyEl = n.querySelector('.jdgm-rev__body, .jdgm-carousel-item__review-body, .jdgm-carousel-item__body');
      var authorEl = n.querySelector('.jdgm-rev__author, .jdgm-carousel-item__reviewer-name, .jdgm-carousel-item__author');
      var locEl = n.querySelector('.jdgm-rev__location, .jdgm-carousel-item__location');
      var timeEl = n.querySelector('.jdgm-rev__timestamp, time[datetime], [data-content], [data-time]');
      var picEl = n.querySelector('.jdgm-rev__pic-img, img.jdgm-rev__pic-img, .jdgm-rev__pics img');
      var verified = !!n.querySelector('.jdgm-rev__buyer-badge, .jdgm--verified') ||
        /jdgm-rev--verified/.test(n.className);
      var prodEl = n.querySelector('.jdgm-rev__product-link, .jdgm-carousel-item__product-title');
      var prodLink = n.querySelector('.jdgm-carousel-item__product, a.jdgm-rev__product-link') || (prodEl && prodEl.closest('a'));
      var prodUrl = prodLink ? (prodLink.getAttribute('href') || '') : '';

      // custom-form "Fit" answer — JM renders cf answers inline
      var fit = '';
      var cf = n.querySelectorAll('.jdgm-rev__custom-fields .jdgm-rev__custom-field, .jdgm--custom-questions li, .jdgm-rev__custom-form *');
      cf.forEach(function (c) {
        var t = (c.textContent || '').trim();
        if (/runs small/i.test(t)) fit = 'Runs small';
        else if (/true to size/i.test(t)) fit = 'True to size';
        else if (/runs large/i.test(t)) fit = 'Runs large';
      });

      var dateRaw = timeEl ? (timeEl.getAttribute('datetime') || timeEl.getAttribute('data-content') || timeEl.getAttribute('data-time') || timeEl.textContent) : '';

      // Body text: JM's endpoint-served widget HTML truncates long bodies and
      // appends its own "Read more"/"Read less" toggle INSIDE .jdgm-rev__body
      // (the metafield-cached page 1 is clean; pages 2+ are not). Strip those
      // control elements before reading textContent, or the toggle label leaks
      // into the quote (seen on SL: "…protect it .Read more").
      var bodyText = '';
      if (bodyEl) {
        var bodyClone = bodyEl.cloneNode(true);
        bodyClone.querySelectorAll('a, button, [class*="read-more"], [class*="read-less"]').forEach(function (junk) {
          var jt = (junk.textContent || '').trim();
          if (/^read\s?(more|less)…?\.?$/i.test(jt) || /read-(more|less)/.test(junk.className || '')) junk.remove();
        });
        bodyText = bodyClone.textContent.trim().replace(/\s*Read more$/i, '');
      }
      out.push({
        reviewId: n.getAttribute('data-review-id') || (n.querySelector('[data-review-id]') && n.querySelector('[data-review-id]').getAttribute('data-review-id')) || '',
        rating: rating,
        title: titleEl ? titleEl.textContent.trim() : '',
        body: bodyText,
        author: authorEl ? authorEl.textContent.trim() : '',
        location: locEl ? locEl.textContent.trim() : '',
        country: locEl ? (locEl.getAttribute('data-country') || '') : '',
        date: dateRaw,
        dateLabel: formatDate(dateRaw),
        verified: verified,
        fit: fit,
        picture: (function () {
          // JM's <img> carries a 160px thumb; the wrapping .jdgm-rev__pic-link
          // carries the full-size (w=1024) URL. Prefer full size; the card
          // renderer downsizes via the imgix w= param.
          var picLink = n.querySelector('.jdgm-rev__pic-link');
          var full = picLink ? (picLink.getAttribute('data-mfp-src') || picLink.getAttribute('href') || '') : '';
          var thumb = picEl ? (picEl.getAttribute('src') || picEl.getAttribute('data-src') || '') : '';
          var u = full || thumb;
          if (/judgeme\.imgix\.net/.test(u)) u = u.replace(/([?&])w=\d+/, '$1w=1024');
          return u;
        })(),
        productTitle: prodEl ? prodEl.textContent.trim() : '',
        productUrl: prodUrl,
        isProductReview: /\/products\//.test(prodUrl)
      });
    });
    return out;
  }

  function parseHistogramFromHTML(html) {
    var hist = null;
    if (!html) return hist;
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var rows = doc.querySelectorAll('.jdgm-histogram__row, [data-rating][data-frequency]');
      if (rows.length) {
        hist = {};
        rows.forEach(function (r) {
          var star = r.getAttribute('data-rating') ||
            (r.querySelector('.jdgm-histogram__rating') || {}).textContent;
          var freq = r.getAttribute('data-frequency') ||
            (r.querySelector('.jdgm-histogram__frequency') || {}).textContent;
          star = parseInt(star, 10); freq = parseInt(freq, 10);
          if (star >= 1 && star <= 5 && !isNaN(freq)) hist[star] = freq;
        });
      }
    } catch (e) {}
    return hist;
  }

  function formatDate(raw) {
    if (!raw) return '';
    raw = String(raw).trim();
    var d = new Date(raw);
    if (isNaN(d.getTime())) {
      // Judge.me timestamp metafield format: "2026-06-01 09:49:45 UTC"
      d = new Date(raw.replace(' ', 'T').replace(/\s*UTC$/i, 'Z'));
    }
    if (isNaN(d.getTime())) {
      var iso = raw.match(/(\d{4})-(\d{2})-(\d{2})/);          // YYYY-MM-DD
      var us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);     // MM/DD/YYYY (carousel data-time)
      if (iso) d = new Date(+iso[1], +iso[2] - 1, +iso[3]);
      else if (us) d = new Date(+us[3], +us[1] - 1, +us[2]);
    }
    if (isNaN(d.getTime())) return raw;
    var locale;
    try { locale = (document.documentElement.lang || undefined); } catch (e) { locale = undefined; }
    try {
      return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  }

  /* ---- adapter: fetch + normalize ----------------------------------------- */
  function endpointFetch(endpoint, params) {
    var qs = Object.keys(params).map(function (k) {
      return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
    }).join('&');
    return fetch(endpoint + (endpoint.indexOf('?') > -1 ? '&' : '?') + qs, {
      headers: { 'Accept': 'application/json' }
    }).then(function (r) { return r.ok ? r.json() : null; });
  }

  function cacheWidgetFetch(el, widgetParams) {
    var domain = shopDomain(el);
    var token = publicToken(el);
    if (!domain || !token) return Promise.resolve(null);
    var base = 'https://cache.judge.me/widgets/shopify/' + encodeURIComponent(domain);
    var params = Object.assign({ public_token: token }, widgetParams);
    var qs = Object.keys(params).map(function (k) {
      return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
    }).join('&');
    return fetch(base + '?' + qs, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // Pull every HTML string out of an unknown-shaped cache response and concat.
  function harvestHTML(json, hint) {
    if (!json) return '';
    var html = '';
    (function walk(v, key) {
      if (typeof v === 'string') {
        if (v.indexOf('<') > -1 && (!hint || (key && key.indexOf(hint) > -1) || v.indexOf('jdgm') > -1)) {
          html += '\n' + v;
        }
      } else if (Array.isArray(v)) {
        v.forEach(function (x) { walk(x, key); });
      } else if (v && typeof v === 'object') {
        Object.keys(v).forEach(function (k) { walk(v[k], k); });
      }
    })(json, '');
    return html;
  }

  /* Public adapter API */
  var Adapter = {
    productReviews: function (el, productId) {
      var endpoint = el.dataset.reviewsEndpoint;
      if (endpoint) {
        return endpointFetch(endpoint, { type: 'product', product_id: productId, page: 1 })
          .then(function (j) {
            if (!j) return null;
            return {
              reviews: (j.reviews || []).map(normalizeJSON),
              histogram: j.histogram || null,
              count: j.count, average: j.average, more: !!j.more
            };
          }).catch(function () { return null; });
      }
      var key = 'slrev:prod:' + productId;
      var cached = cacheGet(key);
      if (cached) return Promise.resolve(cached);
      return cacheWidgetFetch(el, { review_widget_product_ids: productId, reviews_tab: 1 })
        .then(function (json) {
          var html = harvestHTML(json, 'review_widget');
          var res = { reviews: parseReviewsFromHTML(html), histogram: parseHistogramFromHTML(html), more: false };
          if (res.reviews.length) cacheSet(key, res);
          return res;
        });
    },
    featured: function (el) {
      // Prefer a server-rendered in-DOM source (shop judgeme.featured_carousel
      // metafield). Reliable, no token, present in initial HTML.
      var srcSel = el.dataset.jdgmSource;
      var srcEl = srcSel && document.querySelector(srcSel);
      if (srcEl) {
        var fromSrc = parseWidgetEl(srcEl);
        if (fromSrc.length) return Promise.resolve(fromSrc);
      }
      var endpoint = el.dataset.reviewsEndpoint;
      if (endpoint) {
        return endpointFetch(endpoint, { type: 'featured' })
          .then(function (j) { return j ? (j.reviews || []).map(normalizeJSON) : []; })
          .catch(function () { return []; });
      }
      var key = 'slrev:featured';
      var cached = cacheGet(key);
      if (cached) return Promise.resolve(cached);
      return cacheWidgetFetch(el, { featured_carousel: 1 })
        .then(function (json) {
          var html = harvestHTML(json, 'featured');
          var reviews = parseReviewsFromHTML(html);
          if (reviews.length) cacheSet(key, reviews);
          return reviews;
        });
    }
  };

  function normalizeJSON(r) {
    return {
      rating: Number(r.rating),
      title: r.title || '',
      body: r.body || r.content || '',
      author: r.reviewer_name || r.author || r.name || '',
      location: r.location || '',
      country: r.country || r.reviewer_country || '',
      date: r.created_at || r.date || '',
      dateLabel: formatDate(r.created_at || r.date || ''),
      verified: !!(r.verified || r.verified_buyer),
      fit: r.fit || '',
      picture: r.picture || (r.pictures && r.pictures[0] && (r.pictures[0].urls ? r.pictures[0].urls.original : r.pictures[0].url)) || '',
      productTitle: r.product_title || r.productTitle || '',
      productUrl: r.product_url || r.productUrl || '',
      isProductReview: r.isProductReview != null ? r.isProductReview : /\/products\//.test(r.product_url || r.productUrl || '')
    };
  }

  /* ---- expose -------------------------------------------------------------- */
  var SLReviews = window.SLReviews = {
    Adapter: Adapter, starsHTML: starsHTML, seededPick: seededPick,
    countryLabel: countryLabel, shopifyCountry: shopifyCountry, esc: esc,
    formatDate: formatDate
  };

  /* =========================================================================
     COMPONENT 1 — <sl-product-reviews>
     ========================================================================= */
  var FIT_POS = { 'Runs small': 0, 'True to size': 50, 'Runs large': 100 };

  class SLProductReviews extends HTMLElement {
    connectedCallback() {
      if (this._init) return; this._init = true;
      this.count = parseInt(this.dataset.ratingCount || '0', 10) || 0;
      this.average = parseFloat(this.dataset.rating || '0') || 0;
      this.productId = this.dataset.productId || '';
      this.token = this.dataset.jdgmToken || (jdgm().public_token || '');
      this.shop = this.dataset.shopDomain || shopDomain(this);
      this.activeFilter = 'all';
      this.shown = 4;
      this._fetchPage = 0;     // pages pulled from the widget API (per_page 10)
      this._exhausted = false;
      this._loadingMore = false;
      if (this.dataset.demo) { this.dataset.state = 'has'; this.loadOnVisible(); return; }
      if (this.count <= 0) { this.dataset.state = 'empty'; return; }   // Liquid empty state stays
      this.dataset.state = 'has';
      this.loadOnVisible();
    }
    loadOnVisible() {
      var self = this;
      var go = function () { self.load(); };
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) { self._io.disconnect(); go(); }
        }, { rootMargin: '400px' });
        this._io.observe(this);
      } else { go(); }
    }
    disconnectedCallback() {
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._widgetObs) { this._widgetObs.disconnect(); this._widgetObs = null; }
      if (this._widgetTimer) { clearTimeout(this._widgetTimer); this._widgetTimer = null; }
    }
    load() {
      var self = this;
      // 0) demo mode — render a rich sample set to preview the 10+ layout
      if (this.dataset.demo) { this.loadDemo(); return; }
      // 1) optional JSON proxy takes priority (handled inside the adapter)
      if (this.dataset.reviewsEndpoint) { this.loadFromAdapter(); return; }
      // 2) prefer an in-DOM hidden Judge.me widget (guaranteed-correct markup,
      //    and the same element powers the Write-a-review modal)
      var sel = this.dataset.jdgmWidget;
      var widget = sel && document.querySelector(sel);
      if (widget) { this.loadFromWidget(widget); return; }
      // 3) fall back to the public cache-widget fetch
      this.loadFromAdapter();
    }
    loadDemo() {
      var img = this.dataset.demoImage || '';
      function R(rating, title, body, author, loc, dateOffsetDays, verified, fit, pic) {
        var d = new Date(); d.setDate(d.getDate() - dateOffsetDays);
        return {
          rating: rating, title: title, body: body, author: author, location: loc,
          country: '', date: d.toISOString(), dateLabel: SLReviews.formatDate(d.toISOString()),
          verified: verified, fit: fit, picture: pic || '', productTitle: '', productUrl: '', isProductReview: true
        };
      }
      this.reviews = [
        R(5, 'Even more beautiful in person', 'The block print is so detailed and the fit is perfect. I wore it to a family wedding and could not stop getting compliments.', 'Priya R.', 'London', 4, true, 'True to size', img),
        R(5, 'Stunning quality', 'Worth every penny. The fabric is gorgeous, the prints look hand-done, and it washes beautifully. I have worn it three times already this month.', 'Sarah M.', 'Manchester', 9, true, 'True to size', ''),
        R(5, 'My new favourite', 'Threw it on over swimwear, dressed it up for dinner with sandals and jewellery. Incredibly versatile and the colours are magic.', 'Emma T.', 'Sydney', 14, true, 'True to size', img),
        R(4, 'Beautiful, runs slightly small', 'The print is gorgeous and I love the cut. Just a heads-up that it runs a touch small in the bust — I sized up and it fits perfectly.', 'Jessica K.', 'New York', 20, true, 'Runs small', ''),
        R(5, 'Like wearing a piece of art', 'The colours and detail in the print are extraordinary. You can tell it is hand-blocked. Comfortable, flattering, and gets a comment everywhere I go.', 'Anaya P.', 'Dubai', 26, true, 'True to size', ''),
        R(5, 'Holiday staple', 'Packed it for two weeks in Greece and lived in it. Light, cool, and so pretty.', 'Charlotte H.', 'Bristol', 31, true, 'True to size', ''),
        R(4, 'Lovely but long', 'Gorgeous fabric and print. I am petite so I had it taken up an inch, after that it was perfect.', 'Mei L.', 'Singapore', 38, true, 'Runs large', ''),
        R(5, 'Obsessed', 'Second one I have bought. The quality is consistently beautiful and the prints are unlike anything else.', 'Olivia W.', 'Dublin', 44, true, 'True to size', ''),
        R(5, 'Perfect for summer', 'So light and breezy, and the pockets are a lovely surprise. True to size for me.', 'Hannah B.', 'Leeds', 51, true, 'True to size', img),
        R(5, 'Compliments everywhere', 'Wore it to a garden party and three people asked where it was from. The colours are even better in sunlight.', 'Sofia G.', 'Madrid', 58, true, '', ''),
        R(3, 'Pretty but sizing tricky', 'The print is lovely. The fit was a little boxy on me and runs large through the waist, so factor that in.', 'Rachel D.', 'Toronto', 66, true, 'Runs large', ''),
        R(5, 'Will buy again', 'Exactly as pictured, arrived quickly, and the embroidery detail is exquisite.', 'Aisha N.', 'Abu Dhabi', 73, true, 'True to size', '')
      ];
      this.count = this.reviews.length;
      this.average = this.reviews.reduce(function (a, r) { return a + r.rating; }, 0) / this.count;
      this.histogram = null; // tallied in render() since we hold the full set
      this.dataset.state = 'has';
      this.render();
    }
    loadFromAdapter() {
      var self = this;
      SLReviews.Adapter.productReviews(this, this.productId).then(function (data) {
        self.reviews = (data && data.reviews) || [];
        self.histogram = (data && data.histogram) || null;
        if (typeof (data && data.more) === 'boolean') self.more = data.more;
        self.render();
      }).catch(function () { self.render(); });
    }
    loadFromWidget(widget) {
      var self = this;
      var parse = function () {
        var html = widget.innerHTML || '';
        self.reviews = parseWidgetEl(widget);
        self.histogram = parseHistogramFromHTML(html);
        if (self.reviews.length || /jdgm-rev\b/.test(html)) { self.render(); return true; }
        return false;
      };
      if (parse()) return;
      // JM populates asynchronously — observe, with a timeout fallback to fetch
      var done = false;
      this._widgetObs = new MutationObserver(function () {
        if (!self.isConnected) { self._widgetObs.disconnect(); return; }
        if (!done && parse()) { done = true; self._widgetObs.disconnect(); }
      });
      this._widgetObs.observe(widget, { childList: true, subtree: true });
      this._widgetTimer = setTimeout(function () {
        if (done || !self.isConnected) return;
        done = true; if (self._widgetObs) self._widgetObs.disconnect();
        if (!self.reviews || !self.reviews.length) self.loadFromAdapter(); else self.render();
      }, 6000);
    }
    fitAggregate() {
      var withFit = this.reviews.filter(function (r) { return r.fit && FIT_POS[r.fit] != null; });
      if (withFit.length < 5) return null;
      var sum = withFit.reduce(function (a, r) { return a + FIT_POS[r.fit]; }, 0);
      return { pos: Math.round(sum / withFit.length), n: withFit.length };
    }
    filtered() {
      var f = this.activeFilter;
      return this.reviews.filter(function (r) {
        if (f === 'photos') return imgOk(r.picture);
        if (f === 'five') return Math.round(r.rating) === 5;
        if (f === 'verified') return r.verified;
        return true;
      });
    }
    render() {
      var n = this.count;
      var hasList = this.reviews && this.reviews.length;
      var slot = this.querySelector('[data-rev-dynamic]');
      if (!slot) return;

      // Distribution histogram: prefer a real histogram from the data source;
      // otherwise derive it ONLY when we hold the full set (length >= count) so
      // we never draw misleading bars from a partial first page.
      var hist = this.histogram;
      if (!hist && hasList && this.reviews.length >= n) {
        hist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        this.reviews.forEach(function (r) {
          var s = Math.round(r.rating);
          if (s >= 1 && s <= 5) hist[s] += 1;
        });
      }

      // Summary refinements (distribution + fit) only at the right thresholds
      var distHTML = '';
      if (n >= 10 && hist) {
        var total = 0, k;
        for (k = 1; k <= 5; k++) total += (hist[k] || 0);
        if (total > 0) {
          var rows = '';
          for (k = 5; k >= 1; k--) {
            var c = hist[k] || 0;
            var pct = Math.round((c / total) * 100);
            rows += '<div class="sl-rev-dist__row"><span class="sl-rev-dist__num">' + k + '</span>' +
              '<div class="sl-rev-dist__bar"><span style="width:' + pct + '%"></span></div>' +
              '<span class="sl-rev-dist__count">' + c + '</span></div>';
          }
          distHTML = '<div class="sl-rev-dist">' + rows + '</div>';
        }
      }

      var fit = (n >= 5) ? this.fitAggregate() : null;
      var fitHTML = fit ? (
        '<div class="sl-rev-summary__fit"><div class="sl-rev-fit__label">Fit</div>' +
        '<div class="sl-rev-fit__scale"><div class="sl-rev-fit__marker" style="left:' + fit.pos + '%"></div></div>' +
        '<div class="sl-rev-fit__labels"><span>Runs small</span><span class="is-center">True to size</span><span>Runs large</span></div></div>'
      ) : '';

      var summaryCls = 'sl-rev-summary';
      if (!distHTML && !fitHTML) summaryCls += ' sl-rev-summary--slim';
      else if (!fitHTML) summaryCls += ' sl-rev-summary--no-fit';

      var summaryHTML = '<div class="' + summaryCls + '">' +
        '<div class="sl-rev-summary__rating">' + SLReviews.starsHTML(this.average) +
        '<div><div class="sl-rev-summary__avg">' + (Math.round(this.average * 10) / 10) + '</div>' +
        '<div class="sl-rev-summary__count">Based on ' + n + ' review' + (n === 1 ? '' : 's') + '</div></div></div>' +
        (distHTML || (fitHTML ? '<div></div>' : '')) + fitHTML + '</div>';

      // Filters (only where matches exist, and only at n>=5)
      var filtersHTML = '';
      if (n >= 5 && hasList) {
        var defs = [
          { id: 'all', label: 'All reviews' },
          { id: 'photos', label: 'With photos', test: function (r) { return imgOk(r.picture); } },
          { id: 'five', label: '5 stars', test: function (r) { return Math.round(r.rating) === 5; } },
          { id: 'verified', label: 'Verified buyers', test: function (r) { return r.verified; } }
        ];
        var self = this;
        var pills = defs.filter(function (d) { return !d.test || self.reviews.some(d.test); })
          .map(function (d) {
            return '<button type="button" class="sl-rev-pill" data-filter="' + d.id + '" aria-pressed="' +
              (self.activeFilter === d.id ? 'true' : 'false') + '">' + d.label + '</button>';
          }).join('');
        if (pills) filtersHTML = '<div class="sl-rev-filters" role="group" aria-label="Filter reviews">' + pills + '</div>';
      }

      // Featured + grid
      var listHTML = '';
      if (hasList) {
        var list = this.filtered();
        var featured = null;
        if (n >= 10 && this.activeFilter === 'all') {
          // pick a review worthy of the editorial pull-quote treatment:
          // prefer one with a photo and a substantial body, else the longest body.
          var best = -1, bestScore = -1;
          list.forEach(function (r, i) {
            var len = (r.body || '').length;
            if (len < 60) return;                 // too short for a 32px pull-quote
            var score = len + (imgOk(r.picture) ? 1000 : 0);
            if (score > bestScore) { bestScore = score; best = i; }
          });
          if (best === -1) best = 0;              // fall back to the first review
          featured = list[best];
          list = list.slice(0, best).concat(list.slice(best + 1));
        }
        if (featured) listHTML += this.featuredHTML(featured);
        var cards = list.slice(0, this.shown).map(this.cardHTML.bind(this)).join('');
        listHTML += '<div class="sl-rev-grid">' + cards + '</div>';
        // more to reveal locally, OR more still on the server we can fetch
        var moreServer = !this._exhausted && this.reviews.length < n && !!this.token && !!this.shop && !!this.extId();
        if (list.length > this.shown || moreServer) {
          var label = this._loadingMore ? 'Loading…' : 'Show more reviews';
          listHTML += '<div class="sl-rev-more"><button type="button" class="sl-rev-more__btn" data-more' +
            (this._loadingMore ? ' disabled' : '') + '>' + label + '</button></div>';
        }
      } else if (n > 0) {
        // count says reviews exist but the list could not be retrieved yet
        // (data lag, parser miss, source not wired). Keep an honest line rather
        // than a void beneath the summary.
        listHTML = '<div class="sl-rev-loading">Loading the latest reviews…</div>';
      }

      slot.innerHTML = summaryHTML + filtersHTML + listHTML;
      this.wire(slot);
    }
    featuredHTML(r) {
      var photo = imgOk(r.picture) ? '<button type="button" class="sl-rev-photo" data-sl-lightbox data-full="' + SLReviews.esc(fullPic(r.picture)) +
        '" aria-label="View customer photo full size"><img class="sl-rev-featured__photo" src="' + SLReviews.esc(r.picture) +
        '" alt="Customer photo from ' + SLReviews.esc(r.author || 'a verified buyer') + '" width="460" height="575" loading="lazy" decoding="async"></button>' : '';
      var meta = [r.author ? '<b>' + SLReviews.esc(r.author) + '</b>' : '', SLReviews.esc(r.location), r.verified ? 'Verified buyer' : '']
        .filter(Boolean).join(' · ');
      return '<div class="sl-rev-featured' + (photo ? '' : ' sl-rev-featured--no-photo') + '">' + photo +
        '<div class="sl-rev-featured__content">' + SLReviews.starsHTML(r.rating) +
        '<p class="sl-rev-featured__quote">' + SLReviews.esc(r.body) + '</p>' +
        '<div class="sl-rev-featured__meta">' + meta + '</div></div></div>';
    }
    cardHTML(r) {
      var photo = imgOk(r.picture) ? '<button type="button" class="sl-rev-photo" data-sl-lightbox data-full="' + SLReviews.esc(fullPic(r.picture)) +
        '" aria-label="View customer photo full size"><img class="sl-rev-card__photo" src="' + SLReviews.esc(r.picture) +
        '" alt="Customer photo from ' + SLReviews.esc(r.author || 'a verified buyer') + '" width="92" height="115" loading="lazy" decoding="async"></button>' : '';
      var right = [
        r.fit ? '<span class="sl-rev-card__fit">' + SLReviews.esc(r.fit) + '</span>' : '',
        r.verified ? '<span class="sl-rev-card__verified">Verified</span>' : ''
      ].filter(Boolean).join('');
      var who = [SLReviews.esc(r.author), SLReviews.esc(r.location)].filter(Boolean).join(' · ');
      return '<article class="sl-rev-card">' +
        '<div class="sl-rev-card__top">' + SLReviews.starsHTML(r.rating) +
        '<span class="sl-rev-card__date">' + SLReviews.esc(r.dateLabel) + '</span></div>' +
        (r.title ? '<h3 class="sl-rev-card__title">' + SLReviews.esc(r.title) + '</h3>' : '') +
        '<p class="sl-rev-card__body">' + SLReviews.esc(r.body) + '</p>' + photo +
        '<div class="sl-rev-card__footer"><span class="sl-rev-card__who">' + who + '</span>' +
        '<span class="sl-rev-card__metaright">' + right + '</span></div></article>';
    }
    wire(slot) {
      var self = this;
      slot.querySelectorAll('[data-filter]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.activeFilter = btn.dataset.filter; self.shown = 4; self.render();
        });
      });
      var more = slot.querySelector('[data-more]');
      if (more) more.addEventListener('click', function () { self.onShowMore(); });
    }
    extId() { return this.dataset.productId || ''; }
    onShowMore() {
      this.shown += 6;
      // if we're about to run past what's loaded and the server has more, fetch
      if (this.shown >= this.reviews.length && !this._exhausted &&
          this.reviews.length < this.count && this.token && this.shop && this.extId()) {
        this.loadMore();
      } else {
        this.render();
      }
    }
    loadMore() {
      if (this._loadingMore) return;
      this._loadingMore = true;
      this.render();
      var self = this;
      this._fetchPage += 1;
      var u = 'https://judge.me/api/v1/widgets/product_review?api_token=' + encodeURIComponent(this.token) +
        '&shop_domain=' + encodeURIComponent(this.shop) + '&platform=shopify&external_id=' +
        encodeURIComponent(this.extId()) + '&per_page=10&page=' + this._fetchPage;
      fetch(u, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          var html = (j && (j.widget || j.html)) || '';
          var fresh = parseReviewsFromHTML(html);
          var seen = {};
          self.reviews.forEach(function (r) { if (r.reviewId) seen[r.reviewId] = 1; });
          var added = 0;
          fresh.forEach(function (r) {
            if (!r.reviewId || !seen[r.reviewId]) { self.reviews.push(r); if (r.reviewId) seen[r.reviewId] = 1; added++; }
          });
          if (!added || self.reviews.length >= self.count) self._exhausted = true;
          if (!self.histogram) self.histogram = parseHistogramFromHTML(html);
          self._loadingMore = false;
          self.render();
        })
        .catch(function () { self._loadingMore = false; self._exhausted = true; self.render(); });
    }
  }

  /* =========================================================================
     COMPONENT 3 — <sl-featured-reviews>
     ========================================================================= */
  class SLFeaturedReviews extends HTMLElement {
    connectedCallback() {
      if (this._init) return; this._init = true;
      this.count = parseInt(this.dataset.count || '3', 10) || 3;
      var self = this;
      var go = function () { self.load(); };
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (e) { if (e[0].isIntersecting) { self._io.disconnect(); go(); } }, { rootMargin: '300px' });
        this._io.observe(this);
      } else { go(); }
    }
    load() {
      var self = this;
      SLReviews.Adapter.featured(this).then(function (reviews) {
        var usable = (reviews || []).filter(function (r) { return r.body && r.body.trim(); });
        // Fresh random selection on every page load (not day-seeded), so the
        // module rotates through the featured pool each visit.
        var pool = usable.slice();
        for (var i = pool.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp;
        }
        var pick = pool.slice(0, self.count);
        if (!pick.length) { self.remove(); return; }   // hide whole section when none
        self.renderCards(pick);
      }).catch(function () { self.remove(); });
    }
    renderCards(reviews) {
      var grid = this.querySelector('[data-rev-grid]');
      if (!grid) return;
      // Titles here are not structural page headings (this module can sit on a
      // homepage/landing page), so render them as styled paragraphs, not <h3>.
      var shopName = (this.dataset.shopName || '').trim();
      grid.innerHTML = reviews.map(function (r) {
        // show the product reference whenever there is one, except for
        // store-level reviews (whose "product" is the shop itself).
        var isShopReview = r.productTitle && shopName && r.productTitle === shopName;
        var prod = (r.productTitle && !isShopReview)
          ? '<div class="sl-rev-mcard__product">on the ' + SLReviews.esc(r.productTitle) + '</div>' : '';
        var who = [SLReviews.esc(r.author), SLReviews.esc(r.location)].filter(Boolean).join(' · ');
        return '<article class="sl-rev-mcard">' + SLReviews.starsHTML(r.rating) +
          (r.title ? '<p class="sl-rev-mcard__title">' + SLReviews.esc(r.title) + '</p>' : '') +
          '<p class="sl-rev-mcard__quote">' + SLReviews.esc(r.body) + '</p>' +
          '<div class="sl-rev-mcard__attr"><div class="sl-rev-mcard__who">' + who + '</div>' + prod + '</div></article>';
      }).join('');
      this.setAttribute('data-ready', '');
      this.buildDots(grid, reviews.length);
    }
    buildDots(grid, n) {
      var dotsWrap = this.querySelector('[data-rev-dots]');
      if (!dotsWrap || n <= 1) return;
      var cards = Array.prototype.slice.call(grid.children);
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var html = '';
      for (var i = 0; i < n; i++) html += '<button type="button" aria-label="Go to review ' + (i + 1) + '"' + (i === 0 ? ' aria-current="true"' : '') + '></button>';
      dotsWrap.innerHTML = html;
      var dots = Array.prototype.slice.call(dotsWrap.querySelectorAll('button'));
      dots.forEach(function (d, i) {
        d.addEventListener('click', function () {
          cards[i].scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
        });
      });
      // Mark the single most-visible card as current (highest ratio wins).
      this._dotsIO = new IntersectionObserver(function (entries) {
        var top = null;
        entries.forEach(function (en) { if (!top || en.intersectionRatio > top.intersectionRatio) top = en; });
        if (!top || !top.isIntersecting) return;
        var idx = cards.indexOf(top.target);
        dots.forEach(function (d, i) { d.setAttribute('aria-current', i === idx ? 'true' : 'false'); });
      }, { root: grid, threshold: [0.5, 0.75, 1] });
      var io = this._dotsIO;
      cards.forEach(function (c) { io.observe(c); });
    }
    disconnectedCallback() {
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._dotsIO) { this._dotsIO.disconnect(); this._dotsIO = null; }
    }
  }

  /* =========================================================================
     COMPONENT 4 — <sl-cart-review>
     ========================================================================= */
  class SLCartReview extends HTMLElement {
    connectedCallback() {
      if (this._init) return; this._init = true;
      this.load();
      // Cart updates (quantity change, add from a PDP) morph the drawer in
      // place: this element survives with _init set, but the server sends
      // the slot back empty, so the review silently vanished. Watch the
      // slot and re-render whenever the morph empties it.
      var self = this;
      this._mo = new MutationObserver(function () {
        var slot = self.querySelector('[data-rev-slot]');
        if (slot && !slot.firstChild && !self._rendering) self.load();
      });
      this._mo.observe(this, { childList: true, subtree: true });
    }
    disconnectedCallback() {
      if (this._mo) { this._mo.disconnect(); this._mo = null; }
      this._init = false;
    }
    load() {
      // Preferred source: locale-curated metaobject pool, rendered server-side
      // as JSON by the cart snippet (already filtered by market + general
      // fallback). Pick one at random on each load.
      var poolEl = this.querySelector('[data-sl-cart-pool]');
      if (poolEl) {
        var pool;
        try { pool = JSON.parse(poolEl.textContent || '[]'); } catch (e) { pool = []; }
        pool = (pool || []).filter(function (r) { return r.quote && String(r.quote).trim(); });
        if (!pool.length) return;
        var pick = pool[Math.floor(Math.random() * pool.length)];
        this.renderReview({ rating: pick.rating, body: pick.quote, author: pick.reviewer, location: pick.location });
        return;
      }
      // Fallback: Judge.me featured (no locale data — random featured review).
      var self = this;
      SLReviews.Adapter.featured(this).then(function (reviews) {
        reviews = (reviews || []).filter(function (r) { return r.body && r.body.trim(); });
        if (!reviews.length) return;
        var pick = reviews[Math.floor(Math.random() * reviews.length)];
        if (pick) self.renderReview(pick);
      }).catch(function () {});
    }
    renderReview(r) {
      var slot = this.querySelector('[data-rev-slot]');
      if (!slot) return;
      var loc = SLReviews.countryLabel(r.country || r.location);
      this._rendering = true;
      slot.innerHTML = SLReviews.starsHTML(r.rating || 5) +
        '<p class="sl-rev-cart__quote">' + SLReviews.esc(r.body) + '</p>' +
        '<div class="sl-rev-cart__who"><b>' + SLReviews.esc(r.author) + '</b>' +
        (loc ? '<span class="sl-rev-cart__sep"></span>' + SLReviews.esc(loc) : '') + '</div>';
      this.setAttribute('data-ready', '');
      this._rendering = false;
    }
  }

  /* =========================================================================
     COMPONENT 5 — <sl-all-reviews>  (the standalone All Reviews page)
     Two modes:
     • REVEAL (default, no endpoint): the section server-renders a merged,
       de-duped union of the review arrays Judge.me caches in the metafield
       (~30 reviews). This component reveals them in batches and offers
       All / With photos. Used when no proxy is configured.
     • FETCH (when data-reviews-endpoint is set to the Cloudflare Worker URL):
       the Worker holds Judge.me's PRIVATE token and proxies the Reviews Index
       API, which supports server-side rating filtering (1–5 star) + deep
       pagination across ALL reviews. Filter pills and "Show more" then drive
       real fetches; cards are built client-side and escaped (the raw API is
       not XSS-sanitised, so esc() on every field is essential).
     ========================================================================= */
  class SLAllReviews extends HTMLElement {
    connectedCallback() {
      if (this._init) return; this._init = true;
      this.grid = this.querySelector('[data-rev-grid]');
      if (!this.grid) return;
      this.cards = Array.prototype.slice.call(this.grid.querySelectorAll('.sl-rev-card'));
      this.moreWrap = this.querySelector('[data-more-wrap]');
      this.moreBtn = this.querySelector('[data-more]');
      this.progress = this.querySelector('[data-progress]');
      this.filter = 'all';
      this.endpoint = (this.dataset.reviewsEndpoint || '').trim();
      this.fetchMode = !!this.endpoint;
      this.grid.setAttribute('data-ready', '');   // JS now owns card visibility
      // reveal-mode state
      this.initial = parseInt(this.grid.dataset.initial || '10', 10) || 10;
      this.step = 8;
      this.shown = this.initial;
      // fetch-mode state
      this.total = parseInt(this.dataset.total || '0', 10) || 0;
      this.perPage = 10;
      this.page = 1;
      this.rendered = this.cards.length;
      this.loading = false;
      this.wireFilters();
      if (this.moreBtn) this.moreBtn.addEventListener('click', this.onMore.bind(this));
      if (!this.fetchMode) {
        this.applyView();
      } else {
        // Replace the server-rendered seed with page 1 from the Worker, whose
        // cards are link-safe (dead product links already stripped server-side).
        if (this.moreWrap) this.moreWrap.hidden = false;
        this.page = 1;
        this.fetchPage(true);
      }
    }
    wireFilters() {
      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-filter]'), function (btn) {
        btn.addEventListener('click', function () {
          self.filter = btn.dataset.filter;
          Array.prototype.forEach.call(self.querySelectorAll('[data-filter]'), function (b) {
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
          });
          if (self.fetchMode) self.onFilterFetch();
          else { self.shown = self.initial; self.applyView(); }
        });
      });
    }
    onMore() {
      if (this.fetchMode) { this.page += 1; this.fetchPage(false); }
      else { this.shown += this.step; this.applyView(); }
    }

    /* ---- reveal mode ---- */
    matches(card) {
      if (this.filter === 'photos') return card.getAttribute('data-has-photo') === '1';
      return true;
    }
    applyView() {
      var self = this, count = 0, matchTotal = 0;
      this.cards.forEach(function (card) {
        if (self.matches(card)) {
          matchTotal++;
          card.style.display = (count < self.shown) ? '' : 'none';
          count++;
        } else {
          card.style.display = 'none';
        }
      });
      var visible = Math.min(this.shown, matchTotal);
      if (this.progress) {
        this.progress.textContent = matchTotal ? ('Showing ' + visible + ' of ' + matchTotal) : 'No reviews match this filter';
      }
      if (this.moreWrap) this.moreWrap.hidden = false;
      if (this.moreBtn) this.moreBtn.style.display = (this.shown < matchTotal) ? '' : 'none';
    }

    /* ---- fetch mode ---- */
    onFilterFetch() {
      // Worker mode has no "With photos" pill (the reviews API can't filter by
      // has-photo); only 'all' and the 1-5 star ratings reach here.
      this.page = 1;
      this.fetchPage(true);
    }
    setLoading(on) {
      this.loading = on;
      if (this.moreBtn) { this.moreBtn.disabled = on; if (on) this.moreBtn.textContent = 'Loading…'; else this.moreBtn.textContent = 'Show more reviews'; }
    }
    fetchPage(replace) {
      if (this.loading) return;
      this.setLoading(true);
      var self = this;
      var url = this.endpoint + (this.endpoint.indexOf('?') > -1 ? '&' : '?') +
        'page=' + this.page + '&per_page=' + this.perPage +
        (/^[1-5]$/.test(this.filter) ? '&rating=' + this.filter : '');
      fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          var list = (data && data.reviews) || [];
          var html = list.map(self.buildCard.bind(self)).join('');
          if (replace) { self.grid.innerHTML = html; self.rendered = list.length; }
          else { self.grid.insertAdjacentHTML('beforeend', html); self.rendered += list.length; }
          self.setLoading(false);
          var hasMore = !!(data && data.more) && list.length > 0;
          if (self.moreWrap) self.moreWrap.hidden = false;
          if (self.moreBtn) self.moreBtn.style.display = hasMore ? '' : 'none';
          if (self.progress) {
            self.progress.textContent = self.rendered ? ('Showing ' + self.rendered + ' review' + (self.rendered === 1 ? '' : 's'))
              : 'No reviews match this filter';
          }
        })
        .catch(function () {
          self.setLoading(false);
          if (self.progress) self.progress.textContent = 'Could not load more reviews just now.';
          if (self.moreBtn) self.moreBtn.style.display = 'none';
        });
    }
    cleanTitle(title, body) {
      title = (title || '').trim();
      if (!title || title.length > 60) return '';
      if (body && body.indexOf(title) === 0 && title.length > 24) return '';
      return title;
    }
    buildCard(r) {
      var rating = Number(r.rating) || 5;
      var rc = Math.round(rating);
      var hasPhoto = imgOk(r.picture) ? 1 : 0;
      var cardPic = (r.picture || '').replace(/([?&])w=1024\b/, '$1w=320'); // card thumb: 92px @3x
      var photo = hasPhoto ? '<button type="button" class="sl-rev-photo" data-sl-lightbox data-full="' + esc(fullPic(r.picture)) +
        '" aria-label="View customer photo full size"><img class="sl-rev-card__photo" src="' + esc(cardPic) +
        '" alt="Customer photo from ' + esc(r.reviewer_name || 'a verified buyer') + '" width="92" height="115" loading="lazy" decoding="async"></button>' : '';
      var title = this.cleanTitle(r.title, r.body);
      var fitHTML = r.fit ? '<span class="sl-rev-card__fit">' + esc(r.fit) + '</span>' : '';
      var verHTML = r.verified ? '<span class="sl-rev-card__verified">Verified</span>' : '';
      var who = [esc(r.reviewer_name), esc(r.location)].filter(Boolean).join(' · ');
      var isShop = r.product_title && this.dataset.shopName && r.product_title === this.dataset.shopName;
      var prod = (r.product_title && !isShop)
        ? '<p class="sl-rev-card__product">' + (r.product_url ? '<a href="' + esc(r.product_url) + '">' + esc(r.product_title) + '</a>' : esc(r.product_title)) + '</p>'
        : '';
      return '<article class="sl-rev-card" data-rating="' + rc + '" data-has-photo="' + hasPhoto + '">' +
        '<div class="sl-rev-card__top">' + starsHTML(rating) +
        '<span class="sl-rev-card__date">' + esc(formatDate(r.created_at)) + '</span></div>' +
        (title ? '<h3 class="sl-rev-card__title">' + esc(title) + '</h3>' : '') +
        '<p class="sl-rev-card__body">' + esc(r.body) + '</p>' + photo + prod +
        '<div class="sl-rev-card__footer"><span class="sl-rev-card__who">' + who + '</span>' +
        '<span class="sl-rev-card__metaright">' + fitHTML + verHTML + '</span></div></article>';
    }
  }

  /* Write-a-review: our CTAs are styled by us (no jdgm-write-rev-link class, so
     Judge.me's button CSS can't hijack them). On click we reveal the otherwise-
     hidden Judge.me widget mount and trigger ITS write link, so the real JM form
     opens. CSS (.sl-jdgm-form.is-open) masks JM's widget chrome to show only the
     form. */
  /* The mount opens as a MODAL (fixed overlay; full-screen sheet on mobile).
     CSS does the presentation (.sl-jdgm-form.is-open); this controller owns
     open/close, dialog a11y, scroll lock, Esc, backdrop click and a light
     focus trap. JM's form markup itself is untouched. */
  var slModal = { opener: null, prevOverflow: '' };

  function slModalChrome(mount) {
    if (mount.querySelector('.sl-jdgm-form__bar')) return;
    var bar = document.createElement('div');
    bar.className = 'sl-jdgm-form__bar';
    var heading = document.createElement('p');
    heading.className = 'sl-jdgm-form__heading';
    heading.id = 'sl-jdgm-form-heading';
    heading.textContent = 'Write a review';
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'sl-jdgm-form__close';
    close.setAttribute('aria-label', 'Close review form');
    close.setAttribute('data-sl-write-close', '');
    close.innerHTML = '&times;';
    bar.appendChild(heading);
    bar.appendChild(close);
    var panel = mount.querySelector('.jdgm-rev-widg') || mount;
    panel.insertBefore(bar, panel.firstChild);
  }

  function slModalClose() {
    var mount = document.querySelector('.sl-jdgm-form.is-open');
    if (!mount) return;
    mount.classList.remove('is-open');
    mount.classList.remove('sl-jdgm-form--submitted');
    mount.setAttribute('aria-hidden', 'true');
    mount.removeAttribute('role');
    mount.removeAttribute('aria-modal');
    document.documentElement.style.overflow = slModal.prevOverflow;
    if (slModal.opener && slModal.opener.focus) slModal.opener.focus();
    slModal.opener = null;
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-sl-write]');
    if (!t) return;
    e.preventDefault();
    var host = t.closest('sl-product-reviews') || document;
    var mount = host.querySelector ? host.querySelector('.sl-jdgm-form') : null;
    if (!mount) { var link0 = document.querySelector('.jdgm-write-rev-link'); if (link0) link0.click(); return; }
    slModalChrome(mount);
    slModal.opener = t;
    slModal.prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    mount.classList.add('is-open');
    mount.removeAttribute('aria-hidden');
    mount.setAttribute('role', 'dialog');
    mount.setAttribute('aria-modal', 'true');
    mount.setAttribute('aria-labelledby', 'sl-jdgm-form-heading');
    // Judge.me builds/expands its form lazily, so the first click sometimes
    // doesn't open it. Open-if-not-open, then retry a couple of times — and
    // never click when it's already open (which would toggle it shut).
    var formVisible = function () {
      var f = mount.querySelector('.jdgm-form, .jdgm-form-wrapper');
      return f && f.offsetParent !== null;
    };
    var attempt = function () {
      if (formVisible()) return true;
      var link = mount.querySelector('.jdgm-write-rev-link');
      if (link) link.click();
      return false;
    };
    if (!attempt()) {
      setTimeout(function () { if (!formVisible()) attempt(); }, 250);
      setTimeout(function () { if (!formVisible()) attempt(); }, 700);
    }
    var panel = mount.querySelector('.jdgm-rev-widg') || mount;
    panel.setAttribute('tabindex', '-1');
    panel.focus({ preventScroll: true });
  });

  /* close: button or backdrop (the mount itself, outside the panel) */
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-sl-write-close]')) { e.preventDefault(); slModalClose(); return; }
    var open = document.querySelector('.sl-jdgm-form.is-open');
    if (open && e.target === open) slModalClose();
  });

  /* Esc closes; Tab cycles within the open dialog (light focus trap) */
  document.addEventListener('keydown', function (e) {
    var mount = document.querySelector('.sl-jdgm-form.is-open');
    if (!mount) return;
    if (e.key === 'Escape') { slModalClose(); return; }
    if (e.key !== 'Tab') return;
    var focusables = mount.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    var list = Array.prototype.filter.call(focusables, function (f) { return f.offsetParent !== null; });
    if (!list.length) return;
    var firstEl = list[0], lastEl = list[list.length - 1];
    if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
    else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
  });

  /* Delegated smooth-scroll for the star badge -> review block (no inline JS) */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('[data-sl-scroll]');
    if (!a) return;
    var target = document.querySelector(a.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    // Images above the section lazy-load during the scroll and shift the page,
    // so the first landing can be off by their height. Re-align once settled.
    setTimeout(function () {
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, reduce ? 250 : 800);
  });

  /* =========================================================================
     SUBMIT FEEDBACK — Judge.me's own "Review Submitted!" confirmation lives in
     the widget chrome our modal CSS masks, so a successful submit looked like
     nothing happened (the review WAS created). Instead of chasing JM's
     markup (it changes per embed version), watch the network: a click on
     JM's submit button "arms" a 15s window; if a POST to judge.me completes
     inside it, swap the form for an SL-styled confirmation (or show an error
     line on failure). Validation failures never fire a request, and JM's own
     field errors stay visible, so those need nothing from us. */
  var slSubmitArmedAt = 0;
  function slSubmitArmed() { return Date.now() - slSubmitArmedAt < 15000; }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.sl-jdgm-form.is-open .jdgm-submit-rev');
    if (btn) slSubmitArmedAt = Date.now();
  }, true);

  function slIsJmPost(url, method) {
    return String(method || 'GET').toUpperCase() === 'POST' && /judge\.?me/i.test(String(url || ''));
  }

  function slSubmitResult(ok) {
    if (!slSubmitArmed()) return;
    slSubmitArmedAt = 0;
    var mount = document.querySelector('.sl-jdgm-form.is-open');
    if (!mount) return;
    var panel = mount.querySelector('.jdgm-rev-widg') || mount;
    var oldErr = panel.querySelector('.sl-jdgm-form__error');
    if (oldErr) oldErr.remove();
    if (!ok) {
      var err = document.createElement('p');
      err.className = 'sl-jdgm-form__error';
      err.setAttribute('role', 'alert');
      err.textContent = 'Something went wrong sending your review. Please try again.';
      var bar = panel.querySelector('.sl-jdgm-form__bar');
      if (bar && bar.parentNode) bar.parentNode.insertBefore(err, bar.nextSibling);
      else panel.insertBefore(err, panel.firstChild);
      return;
    }
    if (!panel.querySelector('.sl-jdgm-form__success')) {
      var ok0 = document.createElement('div');
      ok0.className = 'sl-jdgm-form__success';
      ok0.setAttribute('role', 'status');
      ok0.innerHTML =
        '<div class="sl-jdgm-form__success-stars" aria-hidden="true">★★★★★</div>' +
        '<p class="sl-jdgm-form__success-h">Thank you for your review</p>' +
        '<p class="sl-jdgm-form__success-p">It has been submitted and will appear here once it has been processed.</p>' +
        '<button type="button" class="sl-jdgm-form__success-btn" data-sl-write-close>Done</button>';
      panel.appendChild(ok0);
    }
    mount.classList.add('sl-jdgm-form--submitted');
    panel.scrollTop = 0;
  }

  (function () {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._slJmReview = slIsJmPost(url, method);
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this._slJmReview && slSubmitArmed()) {
        this.addEventListener('loadend', function () {
          slSubmitResult(this.status >= 200 && this.status < 300);
        });
      }
      return origSend.apply(this, arguments);
    };
    var origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url);
        var method = (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET';
        var watched = slIsJmPost(url, method) && slSubmitArmed();
        var p = origFetch.apply(this, arguments);
        if (watched) {
          p.then(function (res) { slSubmitResult(!!res && res.ok); },
                 function () { slSubmitResult(false); });
        }
        return p;
      };
    }
  })();

  /* =========================================================================
     LIGHTBOX — review photos (cards, featured, gallery strip).
     Photos render inside <button data-sl-lightbox data-full="…"> wrappers
     (JS builders + the Liquid card/gallery). Clicking opens a flat SL-style
     overlay with the full-size image (w=1024 imgix). Singleton element,
     created on first use; Esc / backdrop / × close; scroll locked while open.
     Initial focus goes to the panel (tabindex=-1), NOT the close button —
     programmatic button focus draws the focus ring on iOS. */
  function fullPic(src) {
    src = String(src || '');
    if (!src) return src;
    if (/[?&]w=\d+/.test(src)) return src.replace(/([?&])w=\d+/, '$1w=1024');
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'auto=format&w=1024';
  }

  var slLB = { el: null, opener: null, prevOverflow: '' };

  function slLightboxEl() {
    if (slLB.el) return slLB.el;
    var el = document.createElement('div');
    el.className = 'sl-rev-lb';
    el.setAttribute('hidden', '');
    el.innerHTML =
      '<div class="sl-rev-lb__panel" role="dialog" aria-modal="true" aria-label="Customer photo" tabindex="-1">' +
      '<button type="button" class="sl-rev-lb__close" aria-label="Close photo" data-sl-lb-close>&times;</button>' +
      '<img class="sl-rev-lb__img" alt="">' +
      '<p class="sl-rev-lb__caption"></p>' +
      '</div>';
    document.body.appendChild(el);
    slLB.el = el;
    return el;
  }

  function slLightboxOpen(btn) {
    var innerImg = btn.querySelector('img');
    var full = btn.getAttribute('data-full') || (innerImg && innerImg.currentSrc) || (innerImg && innerImg.src);
    if (!full) return;
    var el = slLightboxEl();
    var img = el.querySelector('.sl-rev-lb__img');
    img.src = full;
    img.alt = innerImg ? innerImg.alt : 'Customer photo';
    el.querySelector('.sl-rev-lb__caption').textContent = btn.getAttribute('data-caption') || (innerImg ? innerImg.alt : '');
    slLB.opener = btn;
    slLB.prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    el.removeAttribute('hidden');
    el.querySelector('.sl-rev-lb__panel').focus({ preventScroll: true });
  }

  function slLightboxClose() {
    var el = slLB.el;
    if (!el || el.hasAttribute('hidden')) return;
    el.setAttribute('hidden', '');
    el.querySelector('.sl-rev-lb__img').removeAttribute('src');
    document.documentElement.style.overflow = slLB.prevOverflow;
    if (slLB.opener && slLB.opener.focus) slLB.opener.focus();
    slLB.opener = null;
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var btn = e.target.closest('[data-sl-lightbox]');
    if (btn) { e.preventDefault(); slLightboxOpen(btn); return; }
    if (e.target.closest('[data-sl-lb-close]')) { slLightboxClose(); return; }
    var lb = slLB.el;
    if (lb && !lb.hasAttribute('hidden') &&
        (e.target === lb || e.target === lb.querySelector('.sl-rev-lb__panel'))) {
      slLightboxClose();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') slLightboxClose();
  });

  function define(name, cls) { if (!customElements.get(name)) customElements.define(name, cls); }
  define('sl-product-reviews', SLProductReviews);
  define('sl-featured-reviews', SLFeaturedReviews);
  define('sl-cart-review', SLCartReview);
  define('sl-all-reviews', SLAllReviews);
})();
